import { PresentationSectionProps } from "../PresentationSectionProps";
import { FreeHtmlTextSectionView } from "@cma/presentation-generation-logic/models/section-view";
import {
  AIAssistedTextSectionConfig,
  FreeHtmlTextSectionConfig,
} from "@cma/presentation-generation-logic/models/section-config";
import { TextEditor, TextEditorProps } from "./TextEditor";
import { useCallback, useRef, useState, useEffect } from "react";
import { useContentHydration } from "./useContentHydration";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import { IconButton, Typography, Tooltip } from "@mui/material";
import PrintDisabledIcon from "@mui/icons-material/PrintDisabled";
import { Button } from "@avenue-8/sidekick-ui";
import { AIAssistedTextSectionView } from "@cma/presentation-generation-logic/sections/ai-assisted-text/ai-assisted-text-section-view";
import {
  ActionsContainer,
  Container,
  FreeTextHtmlContent,
  TopBarContainer,
} from "./FreeHtmlText.styles";

interface Props
  extends PresentationSectionProps<
    FreeHtmlTextSectionView | AIAssistedTextSectionView,
    FreeHtmlTextSectionConfig | AIAssistedTextSectionConfig
  > {
  isEditing?: boolean;
  onIsEditingChange?: (isEditing: boolean) => void;
}

export const FreeHtmlText = (props: Props) => {
  const {
    sectionView: { id, htmlContent },
    onUpdateSection,
    sectionConfig,
    isEditing = false,
    onIsEditingChange,
  } = props;
  const [exceedsPageHeight, setExceedsPageHeight] = useState(false);
  const editorRef: TextEditorProps["editorRef"] = useRef(null);
  const { contentRef } = useContentHydration([htmlContent, isEditing]);

  useEffect(() => {
    if (!contentRef?.current) return;

    const observer = new ResizeObserver(() => {
      const height = contentRef.current?.scrollHeight || 0;
      setExceedsPageHeight(height > 627);
    });

    observer.observe(contentRef.current);
    return () => observer.disconnect();
  }, [contentRef]);

  const handleSave = useCallback(() => {
    const html = editorRef.current?.getContent() || "";
    onUpdateSection({ sectionConfig: { ...sectionConfig, htmlContent: html } });
    onIsEditingChange?.(false);
  }, [onUpdateSection, sectionConfig, onIsEditingChange]);

  return (
    <Container id={id} $isEditing={isEditing} className="section-edit-wrapper">
      <TopBarContainer className="top-bar-container" $isEditing={isEditing}>
        <Typography fontWeight={700}></Typography>
        <ActionsContainer>
          {!isEditing && exceedsPageHeight && (
            <Tooltip title="Content won't fit on one printed page. Split into multiple sections for better readability.">
              <IconButton size="small" sx={{ cursor: "help" }}>
                <PrintDisabledIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {isEditing && (
            <>
              <Button
                onClick={handleSave}
                size="small"
                startIcon={<SaveIcon fontSize="small" />}
                variant="text"
                sx={{ padding: "0.25rem 0.5rem" }}
              >
                Save
              </Button>
              <IconButton
                onClick={() => onIsEditingChange?.(false)}
                size="small"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </>
          )}
        </ActionsContainer>
      </TopBarContainer>

      {isEditing ? (
        <TextEditor
          htmlContent={htmlContent}
          editorRef={editorRef}
          selectAllOnLoad
        />
      ) : (
        <FreeTextHtmlContent
          ref={contentRef}
          dangerouslySetInnerHTML={{ __html: htmlContent }}
        />
      )}
    </Container>
  );
};
